import React, { useMemo } from 'react';

import Button from '../Button';
import { Card } from '../Card';
import { IconFigure } from '../Icons';
import { IconAdmin } from '../Icons/IconAdmin';
import Tags from '../Tags';

import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useUsersProfilePic } from '@/hooks/useUsersProfilePic';
import { ActionsTourneys, GenericItemTourneysData } from '@/types/tda';
import { EnumContextTourneys } from '@/types/team';
import { cn, STATUS_TOURNEY_INVITATION } from '@/utils';

interface Props extends GenericItemTourneysData {
  context?: EnumContextTourneys;
  onClick: ({ id, action }: ActionsTourneys) => void;
  classNames?: {
    secondaryText?: string;
    subtitle?: string;
  };
}

export const TourneyListItems = React.memo(
  ({
    title,
    subtitle,
    secundaryText,
    textCentered,
    action,
    id,
    context,
    onClick,
    status,
    disabled,
    isVip,
    variant = 'default',
    classNames = {},
    estadistica,
    profilePicUrl,
    profileName,
    withAvatar,
    isAdmin,
  }: Props) => {
    const { isDesktop } = useIsDesktop();

    const { setProfilePic } = useUsersProfilePic();

    const statusId = useMemo(() => {
      return status === STATUS_TOURNEY_INVITATION.REJECTED ? 102 : 103;
    }, [status]);

    const badges = (
      <>
        {isAdmin && (
          <figure className="w-6 lg:mx-1 ml-1 shrink-0">
            <IconAdmin />
          </figure>
        )}
        {isVip && (
          <figure className="w-5 lg:mx-1 mr-1 shrink-0">
            <IconFigure />
          </figure>
        )}
      </>
    );

    return (
      <Card
        className={cn(
          'flex items-center justify-between bg-white w-full pl-5 pr-4 pt-6 pb-5 mb-[1px] min-h-[95px] lg:h-auto',
          {
            'pl-0': withAvatar,
            'rounded-none bg-white lg:px-9':
              context &&
              [
                EnumContextTourneys.MainContentHome,
                EnumContextTourneys.Admin,
                EnumContextTourneys.Sidebar,
              ].includes(context),
            '!pl-0':
              withAvatar &&
              context &&
              [EnumContextTourneys.Admin, EnumContextTourneys.Sidebar].includes(
                context
              ),
            'lg:px-4':
              variant === 'two-actions' &&
              context === EnumContextTourneys.Sidebar,
          }
        )}
      >
        {withAvatar && (
          <figure className="px-3">
            {setProfilePic({
              url: profilePicUrl,
              dtName: String(profileName),
              alt: String(title),
              size: 40,
            })}
          </figure>
        )}
        <div
          className={cn(
            'flex space-y-2 flex-col flex-1 lg:w-full min-w-[160px] lg:max-w-[190px] h-full',
            {
              'lg:min-w-[140px] lg:max-w-none': withAvatar,
            }
          )}
        >
          <div
            className={cn({
              'flex flex-row items-center': isDesktop && (isVip || isAdmin),
            })}
          >
            <p
              className={cn(
                'text-sm font-bold text-gdt-gray-2 truncate leading-tight',
                {
                  'text-gdt-gray':
                    context &&
                    [
                      EnumContextTourneys.MainContentHome,
                      EnumContextTourneys.Admin,
                      EnumContextTourneys.Sidebar,
                    ].includes(context) &&
                    isDesktop,
                }
              )}
            >
              {title}
            </p>
            {isDesktop && badges}
          </div>

          {subtitle && !textCentered && (
            <p
              className={cn(
                'text-3xs font-semibold text-gdt-gray-2 leading-none',
                {
                  'text-gdt-blue text-2xs':
                    variant === 'two-actions' &&
                    context &&
                    ![
                      EnumContextTourneys.Sidebar,
                      EnumContextTourneys.Admin,
                    ].includes(context),
                },
                classNames.subtitle
              )}
            >
              {subtitle}
            </p>
          )}
          {secundaryText && (
            <div
              className={cn({
                'flex flex-row items-center': !isDesktop && (isVip || isAdmin),
              })}
            >
              <p
                className={cn(
                  'text-2xs font-semibold text-gdt-gray-4 leading-none flex lg:block items-center',
                  classNames?.secondaryText
                )}
              >
                {!isDesktop && badges}
                {secundaryText}
              </p>
            </div>
          )}
          {estadistica && estadistica.length > 0 && (
            <p className="text-blue-500 text-3xs">{estadistica}</p>
          )}
        </div>
        {textCentered && (
          <div className="lg:text-center">
            <p
              className={cn(
                'text-sm font-semibold text-gdt-gray-3 leading-none',
                {
                  'text-center': variant === 'two-actions',
                }
              )}
            >
              {textCentered}
            </p>
          </div>
        )}
        {variant === 'default' && (
          <div className="lg:w-[202px] text-right">
            <Button
              className={cn('w-[115px] h-[36px] px-0 text-xs', {
                'p-0': disabled,
              })}
              disabled={disabled}
              type="button"
              variant={action?.variantButton || 'primary'}
              onClick={() => (id && onClick({ id })) || undefined}
            >
              {disabled ? action?.txtActionDisabled : action?.txtAction}
            </Button>
          </div>
        )}
        {variant === 'show-status' && (
          <div className="lg:w-[202px] text-right">
            <Tags.Alert statusId={statusId}>{status}</Tags.Alert>
          </div>
        )}
        {variant === 'two-actions' && (
          <div className="space-x-2 lg:w-[202px] text-right">
            {isDesktop && (
              <Button
                className={cn('w-[97px] h-[36px] px-0 text-xs', {
                  'p-0': disabled,
                  'lg:w-[84px]': context === EnumContextTourneys.Admin,
                })}
                disabled={disabled}
                type="button"
                variant="danger"
                onClick={() =>
                  (id && onClick({ id, action: 'reject' })) || undefined
                }
              >
                Rechazar
              </Button>
            )}
            <Button
              className={cn('w-[97px] h-[36px] px-0 text-xs', {
                'p-0': disabled,
                'w-[115px]': !isDesktop,
                'lg:w-[84px]': context === EnumContextTourneys.Admin,
              })}
              disabled={disabled}
              type="button"
              variant="secondary"
              onClick={() =>
                (id && onClick({ id, action: 'accept' })) || undefined
              }
            >
              {isDesktop ? 'Aceptar' : 'Ver solicitud'}
            </Button>
          </div>
        )}
      </Card>
    );
  }
);

TourneyListItems.displayName = 'TourneyListItems';
